import React, { useState, useEffect, useCallback } from 'react';
import { storage } from '../utils/storage';
import { Match, MatchEvent, Season, Player } from '../types';
import { removeMatchStats, updateMatchStats, applyMatchStats, getPlayerDisplayName } from '../utils/matchStats';
import PlayerProfileModal from '../components/PlayerProfileModal';
import TeamLogo from '../components/TeamLogo';
import GoalIcon from '../components/GoalIcon';
import AssistIcon from '../components/AssistIcon';
import { toTurkishUpperCase, turkishSort } from '../utils/turkishUtils';
import { ensureHttps } from '../utils/urlUtils';
import './HistoryPage.css';

const HistoryPage: React.FC = () => {
  const [matches, setMatches] = useState<Match[]>([]);
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [selectedSeason, setSelectedSeason] = useState<string>('');
  const [searchDate, setSearchDate] = useState<string>('');
  const [showAddMatch, setShowAddMatch] = useState(false);
  const [editingMatch, setEditingMatch] = useState<Match | null>(null);
  const [selectedMatch, setSelectedMatch] = useState<Match | null>(null);
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [players, setPlayers] = useState<Player[]>([]);
  const isAdmin = storage.isAdminMode();

  const loadData = async () => {
    try {
      // Verileri API'den yükle (cache otomatik yönetiliyor)
      const [allMatches, allPlayers, allSeasons] = await Promise.all([
        storage.getMatches().catch(err => {
          console.error('[HistoryPage] Maç yükleme hatası:', err);
          return [];
        }),
        storage.getPlayers().catch(err => {
          console.error('[HistoryPage] Oyuncu yükleme hatası:', err);
          return [];
        }),
        storage.getSeasons().catch(err => {
          console.error('[HistoryPage] Sezon yükleme hatası:', err);
          return [];
        }),
      ]);
      
      // Hemen göster (UI hızlı gösterilsin)
      setMatches(Array.isArray(allMatches) ? allMatches : []);
      setPlayers(Array.isArray(allPlayers) ? allPlayers : []);
      setSeasons(Array.isArray(allSeasons) ? allSeasons : []);
      
      const currentSeasonId = await storage.getCurrentSeason().catch(err => {
        console.error('[HistoryPage] Mevcut sezon yükleme hatası:', err);
        return '';
      });
      if (currentSeasonId) {
        setSelectedSeason(currentSeasonId);
      }
    } catch (error) {
      console.error('[HistoryPage] loadData hatası:', error);
      // Hata durumunda en azından boş array'ler set et
      setMatches([]);
      setPlayers([]);
      setSeasons([]);
    }
  };

  const filterMatches = useCallback(async () => {
    try {
      // API'den yükle (cache otomatik yönetiliyor)
      let filtered = await storage.getMatches().catch(err => {
        console.error('[HistoryPage] filterMatches - Maç yükleme hatası:', err);
        return [];
      });
      
      if (!Array.isArray(filtered)) {
        filtered = [];
      }
    
    // Eğer tarih araması varsa, sezon filtresini yoksay ve sadece tarihe göre filtrele
    if (searchDate) {
      filtered = filtered.filter((m: Match) => m.date === searchDate);
    } else if (selectedSeason) {
      // Tarih araması yoksa, sezona göre filtrele
      filtered = filtered.filter((m: Match) => m.seasonId === selectedSeason);
    }
    
      setMatches(filtered.sort((a: Match, b: Match) => new Date(b.date).getTime() - new Date(a.date).getTime()));
    } catch (error) {
      console.error('[HistoryPage] filterMatches hatası:', error);
      setMatches([]);
    }
  }, [selectedSeason, searchDate]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    filterMatches();
  }, [selectedSeason, searchDate, filterMatches]);

  const handlePlayerClick = (playerId: string) => {
    const player = players.find(p => p.id === playerId);
    if (player) {
      setSelectedPlayer(player);
    }
  };

  const getPlayerName = (playerId: string) => {
    return toTurkishUpperCase(getPlayerDisplayName(players, playerId));
  };

  return (
    <div className="history-page">
      <h1 className="page-title">MAÇ GEÇMİŞİ</h1>
      
      <div className="current-season-info">
        <h2>Mevcut Sezon: {seasons.length > 0 ? (seasons.find(s => s.isActive)?.name || selectedSeason || 'Yükleniyor...') : 'Yükleniyor...'}</h2>
      </div>

      <div className="filters">
        {isAdmin && (
          <div className="current-season-filter">
            <label>
              Mevcut Sezon:
              <select 
                value={selectedSeason} 
                onChange={async (e) => {
                  const newSeasonId = e.target.value;
                  await storage.setCurrentSeason(newSeasonId);
                  setSelectedSeason(newSeasonId);
                  // Sayfayı yenile
                  window.location.reload();
                }}
              >
                {seasons.map(season => (
                  <option key={season.id} value={season.id}>{season.name}</option>
                ))}
              </select>
            </label>
          </div>
        )}
        
        <div className="season-filter">
          <label>
            Sezon Seç:
            <select 
              value={selectedSeason} 
              onChange={(e) => setSelectedSeason(e.target.value)}
            >
              {seasons.map(season => (
                <option key={season.id} value={season.id}>{season.name}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="date-filter">
          <label>
            Tarih Ara:
            <input
              type="date"
              value={searchDate}
              onChange={(e) => setSearchDate(e.target.value)}
            />
          </label>
          {searchDate && (
            <button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setSearchDate('');
              }}
              className="clear-date-button"
            >
              Temizle
            </button>
          )}
        </div>
      </div>

      {isAdmin && (
        <div className="admin-actions">
          <button 
            className="add-match-button" 
            onClick={() => setShowAddMatch(true)}
          >
            Maç Ekle
          </button>
          <button 
            className="manage-seasons-button"
            onClick={async () => {
              try {
                const seasonName = prompt('Yeni sezon adı:');
                if (!seasonName || seasonName.trim() === '') {
                  return; // Kullanıcı iptal etti veya boş bıraktı
                }
                
                console.log('[HistoryPage] Yeni sezon ekleniyor:', seasonName);
                
                const newSeason: Season = {
                  id: `season-${Date.now()}`,
                  name: seasonName.trim(),
                  isActive: false,
                };
                
                const updatedSeasons = [...seasons, newSeason];
                
                // API'ye kaydet
                await storage.saveSeasons(updatedSeasons);
                console.log('[HistoryPage] Sezonlar kaydedildi:', updatedSeasons.length);
                
                // Oyuncuların istatistiklerini sıfırlama (yeni sezon için)
                // ÖNEMLİ: Sunucudan taze oyuncu listesi (state'teki eski liste tam kayıtta silinen/eklenen tutarsızlığına yol açmasın)
                const freshPlayers = await storage.getPlayers({ forceRefresh: true });
                const updatedPlayers = freshPlayers.map(p => ({
                  ...p,
                  // Mevcut görselleri koru
                  image: p.image,
                  profileImage: p.profileImage,
                  playerCard: p.playerCard,
                  playerCards: p.playerCards,
                  stats: {
                    ...p.stats,
                    [newSeason.id]: {
                      matches: 0,
                      wins: 0,
                      draws: 0,
                      losses: 0,
                      goals: 0,
                      assists: 0,
                      ownGoals: 0,
                      points: 0,
                      blackCards: 0,
                      mvdpCards: 0,
                    },
                  },
                }));
                
                // Oyuncuları kaydet
                await storage.savePlayers(updatedPlayers);
                console.log('[HistoryPage] Oyuncular güncellendi');
                
                // API'nin güncellenmesi için kısa bir bekleme
                await new Promise(resolve => setTimeout(resolve, 1000));
                
                // Verileri yeniden yükle (API'den güncel veri çekilsin)
                await loadData();
                
                // State'i de güncelle (hemen görünsün)
                setSeasons(updatedSeasons);
                
                // Başarı mesajı
                alert(`"${seasonName}" sezonu başarıyla eklendi!`);
              } catch (error: any) {
                console.error('[HistoryPage] Yeni sezon ekleme hatası:', error);
                const errorMessage = error?.message || 'Bilinmeyen hata';
                alert('Yeni sezon eklenirken bir hata oluştu: ' + errorMessage + '\n\nLütfen Console\'u (F12) kontrol edin.');
              }
            }}
          >
            Yeni Sezon Ekle
          </button>
          <button 
            className="delete-season-button"
            onClick={async () => {
              if (seasons.length === 0) {
                alert('Silinecek sezon bulunmuyor!');
                return;
              }
              
              const seasonOptions = seasons.map(s => s.name).join('\n');
              const seasonName = prompt(`Silinecek sezon adını girin:\n\n${seasonOptions}`);
              
              if (seasonName) {
                const seasonToDelete = seasons.find(s => s.name === seasonName);
                if (!seasonToDelete) {
                  alert('Sezon bulunamadı!');
                  return;
                }
                
                // Aktif sezon silinemez kontrolü
                if (seasonToDelete.isActive) {
                  alert('Aktif sezon silinemez! Önce başka bir sezonu aktif yapın.');
                  return;
                }
                
                const confirmDelete = window.confirm(
                  `"${seasonName}" sezonunu silmek istediğinize emin misiniz?\n\nBu sezona ait tüm maçlar ve istatistikler silinecektir!`
                );
                
                if (confirmDelete) {
                  // Sezonu sil
                  const updatedSeasons = seasons.filter(s => s.id !== seasonToDelete.id);
                  await storage.saveSeasons(updatedSeasons);
                  setSeasons(updatedSeasons);
                  
                  // Bu sezona ait maçları sil
                  const allMatches = await storage.getMatches({ forceRefresh: true });
                  const updatedMatches = allMatches.filter(m => m.seasonId !== seasonToDelete.id);
                  const deletedMatchIds = allMatches
                    .filter(m => m.seasonId === seasonToDelete.id)
                    .map(m => m.id);
                  await storage.saveMatches(updatedMatches, { deleteIds: deletedMatchIds });
                  
                  // Verileri yeniden yükle
                  await loadData();
                  await filterMatches();
                  
                  // Oyuncuların bu sezona ait istatistiklerini sil (taze liste ile kaydet)
                  const freshPlayers = await storage.getPlayers({ forceRefresh: true });
                  const updatedPlayers = freshPlayers.map(p => {
                    const newStats = { ...p.stats };
                    delete newStats[seasonToDelete.id];
                    return {
                      ...p,
                      stats: newStats
                    };
                  });
                  await storage.savePlayers(updatedPlayers);
                  
                  // Eğer seçili sezon silinen sezon ise, başka bir sezona geç
                  if (selectedSeason === seasonToDelete.id) {
                    const newSelectedSeason = updatedSeasons.length > 0 ? updatedSeasons[0].id : '';
                    setSelectedSeason(newSelectedSeason);
                  }
                  
                  await loadData();
                  alert(`"${seasonName}" sezonu başarıyla silindi!`);
                }
              }
            }}
          >
            Sezon Sil
          </button>
        </div>
      )}

      {showAddMatch && isAdmin && (
        <AddMatchModal
          onClose={async () => {
            setShowAddMatch(false);
            await loadData();
            await filterMatches();
          }}
          seasons={seasons}
          players={players}
        />
      )}

      <div className="matches-list">
        {matches.length === 0 ? (
          <div className="no-matches">Henüz maç eklenmemiş.</div>
        ) : (
          matches.map(match => {
            const matchSeason = seasons.find(s => s.id === match.seasonId);
            const seasonName = matchSeason?.name || '';
            
            return (
              <div 
                key={match.id} 
                className="match-card"
                onClick={() => setSelectedMatch(match)}
              >
                <div className="match-header">
                  {seasonName && (
                    <span className="match-season">
                      {seasonName}
                    </span>
                  )}
                  <span className="match-date">
                    {new Date(match.date).toLocaleDateString('tr-TR')}
                  </span>
                  <span className="match-score">
                    <TeamLogo team="black" size={42} />
                    <span className="team-name">SİYAH</span> {match.blackScore} - {match.whiteScore} <span className="team-name">BEYAZ</span>
                    <TeamLogo team="white" size={42} />
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>

      {selectedMatch && !editingMatch && (
        <MatchDetailModal
          match={selectedMatch}
          seasons={seasons}
          onClose={() => setSelectedMatch(null)}
          onPlayerClick={handlePlayerClick}
          getPlayerName={getPlayerName}
          onEdit={() => {
            setEditingMatch(selectedMatch);
            setSelectedMatch(null);
          }}
          onDelete={async () => {
            if (window.confirm('Bu maçı silmek istediğinize emin misiniz? Tüm istatistikler güncellenecektir.')) {
              try {
                await removeMatchStats(selectedMatch);
                const allMatches = await storage.getMatches({ forceRefresh: true });
                const updatedMatches = allMatches.filter(m => m.id !== selectedMatch.id);
                await storage.saveMatches(updatedMatches, { deleteIds: [selectedMatch.id] });
                setSelectedMatch(null);
                await loadData();
                alert('Maç başarıyla silindi!');
              } catch (error: any) {
                if (error.name === 'QuotaExceededError' || error.message?.includes('quota')) {
                  alert('Depolama alanı dolu! Lütfen eski maçlardaki görselleri silin.');
                } else {
                  alert('Maç silinirken bir hata oluştu: ' + error.message);
                }
              }
            }
          }}
        />
      )}

      {editingMatch && (
        <EditMatchModal
          match={editingMatch}
          onClose={() => {
            setEditingMatch(null);
            loadData();
          }}
          seasons={seasons}
          players={players}
        />
      )}

      {selectedPlayer && (
        <PlayerProfileModal
          player={selectedPlayer}
          onClose={() => setSelectedPlayer(null)}
          isCompact={false}
          onCardClick={(match) => {
            setSelectedPlayer(null);
            setSelectedMatch(match);
          }}
        />
      )}
    </div>
  );
};

interface AddMatchModalProps {
  onClose: () => void;
  seasons: Season[];
  players: Player[];
}

const AddMatchModal: React.FC<AddMatchModalProps> = ({ onClose, seasons, players }) => {
  const [matchDate, setMatchDate] = useState<string>('');
  const [selectedSeasonId, setSelectedSeasonId] = useState<string>('');
  
  useEffect(() => {
    const initSeason = async () => {
      const currentSeasonId = await storage.getCurrentSeason();
      setSelectedSeasonId(currentSeasonId);
    };
    initSeason();
  }, []);
  const [blackTeam, setBlackTeam] = useState<string[]>([]);
  const [whiteTeam, setWhiteTeam] = useState<string[]>([]);
  const [events, setEvents] = useState<MatchEvent[]>([]);
  const [blackCard, setBlackCard] = useState<string>('');
  const [mvdp, setMvdp] = useState<string>('');
  const [blackCardImage, setBlackCardImage] = useState<string>('');
  const [mvdpImage, setMvdpImage] = useState<string>('');
  const [matchImage, setMatchImage] = useState<string>('');
  const [showEventForm, setShowEventForm] = useState(false);
  const [newEvent, setNewEvent] = useState<Partial<MatchEvent>>({
    minute: 1,
    team: 'black',
    ownGoal: false,
  });
  const [isGuestScorer, setIsGuestScorer] = useState(false);
  const [isGuestAssist, setIsGuestAssist] = useState(false);

  const togglePlayerInTeam = (playerId: string, team: 'black' | 'white') => {
    if (team === 'black') {
      setBlackTeam(prev => {
        const currentTeam = prev || [];
        return currentTeam.includes(playerId) 
          ? currentTeam.filter(id => id !== playerId)
          : [...currentTeam, playerId];
      });
    } else {
      setWhiteTeam(prev => {
        const currentTeam = prev || [];
        return currentTeam.includes(playerId) 
          ? currentTeam.filter(id => id !== playerId)
          : [...currentTeam, playerId];
      });
    }
  };

  const addEvent = () => {
    // Gol atan kişi seçilmeli (normal oyuncu veya misafir)
    if (!isGuestScorer && !newEvent.scorerId) return;
    if (isGuestScorer && !newEvent.guestScorerName) return;
    
    const event: MatchEvent = {
      id: `event-${Date.now()}`,
      minute: newEvent.minute || 1,
      team: newEvent.team || 'black',
      scorerId: isGuestScorer ? undefined : newEvent.scorerId,
      assistId: isGuestAssist ? undefined : newEvent.assistId,
      guestScorerName: isGuestScorer ? newEvent.guestScorerName : undefined,
      guestAssistName: isGuestAssist ? newEvent.guestAssistName : undefined,
      ownGoal: newEvent.ownGoal || false,
    };
    
    setEvents([...events, event]);
    setNewEvent({
      minute: 1,
      team: 'black',
      ownGoal: false,
    });
    setIsGuestScorer(false);
    setIsGuestAssist(false);
    setShowEventForm(false);
  };

  const saveMatch = async () => {
    if (!matchDate || !selectedSeasonId) {
      alert('Lütfen tarih ve sezon seçin!');
      return;
    }

    // Kendi kalesine goller de skora dahil edilir (oyuncu istatistiğine yansımaz ama skora yansır)
    const blackScore = events.filter(e => 
      e.team === 'black'
    ).length;
    const whiteScore = events.filter(e => 
      e.team === 'white'
    ).length;

    // Events'teki undefined değerleri temizle
    const cleanedEvents = events.map(event => {
      const cleaned: any = {
        id: event.id,
        minute: event.minute,
        team: event.team,
        ownGoal: event.ownGoal,
      };
      if (event.scorerId) cleaned.scorerId = event.scorerId;
      if (event.assistId) cleaned.assistId = event.assistId;
      if (event.guestScorerName) cleaned.guestScorerName = event.guestScorerName;
      if (event.guestAssistName) cleaned.guestAssistName = event.guestAssistName;
      return cleaned;
    });

    const match: Match = {
      id: `match-${Date.now()}`,
      date: matchDate,
      seasonId: selectedSeasonId,
      blackTeam,
      whiteTeam,
      blackScore,
      whiteScore,
      events: cleanedEvents,
    };
    
    // Opsiyonel alanları sadece varsa ekle
    if (blackCard) match.blackCard = blackCard;
    if (mvdp) match.mvdp = mvdp;
    if (blackCardImage && blackCardImage.trim() !== '') {
      match.blackCardImage = blackCardImage;
      console.log('[AddMatchModal] Siyah kart görseli kaydediliyor (uzunluk:', blackCardImage.length, ')');
    }
    if (mvdpImage && mvdpImage.trim() !== '') {
      match.mvdpImage = mvdpImage;
      console.log('[AddMatchModal] MVDP kart görseli kaydediliyor (uzunluk:', mvdpImage.length, ')');
    }
    if (matchImage && matchImage.trim() !== '') {
      match.matchImage = matchImage;
      console.log('[AddMatchModal] Maç kadro görseli kaydediliyor (uzunluk:', matchImage.length, ')');
    }
    
    console.log('[AddMatchModal] Maç kaydediliyor:', {
      id: match.id,
      date: match.date,
      hasBlackCardImage: !!match.blackCardImage,
      hasMvdpImage: !!match.mvdpImage,
      hasMatchImage: !!match.matchImage,
    });

    try {
      const allMatches = await storage.getMatches({ forceRefresh: true });
      allMatches.push(match);
      await storage.saveMatches(allMatches);
    } catch (error: any) {
      if (error.name === 'QuotaExceededError' || error.message?.includes('quota')) {
        alert('Depolama alanı dolu! Lütfen eski maçlardaki görselleri silin veya görselleri daha küçük boyutlarda yükleyin.');
        return;
      }
      alert('Maç kaydedilirken bir hata oluştu: ' + error.message);
      return;
    }

    try {
      // İstatistikleri uygula
      await applyMatchStats(match);

      // Kart görsellerini oyunculara ekle
      if (blackCard && blackCardImage) {
        const allPlayers = await storage.getPlayers({ forceRefresh: true });
        const updatedPlayers = allPlayers.map(p => {
          if (p.id === blackCard) {
            const currentSeasonId = selectedSeasonId;
            const currentCards = Array.isArray(p.playerCards) 
              ? {} 
              : (p.playerCards as { [seasonId: string]: string[] }) || {};
            const seasonCards = currentCards[currentSeasonId] || [];
            if (!seasonCards.includes(blackCardImage)) {
              return {
                ...p,
                playerCards: {
                  ...currentCards,
                  [currentSeasonId]: [...seasonCards, blackCardImage]
                }
              };
            }
          }
          return p;
        });
        await storage.savePlayers(updatedPlayers);
      }

      if (mvdp && mvdpImage) {
        const allPlayers = await storage.getPlayers({ forceRefresh: true });
        const updatedPlayers = allPlayers.map(p => {
          if (p.id === mvdp) {
            const currentSeasonId = selectedSeasonId;
            const currentCards = Array.isArray(p.playerCards) 
              ? {} 
              : (p.playerCards as { [seasonId: string]: string[] }) || {};
            const seasonCards = currentCards[currentSeasonId] || [];
            if (!seasonCards.includes(mvdpImage)) {
              return {
                ...p,
                playerCards: {
                  ...currentCards,
                  [currentSeasonId]: [...seasonCards, mvdpImage]
                }
              };
            }
          }
          return p;
        });
        await storage.savePlayers(updatedPlayers);
      }

      alert('Maç başarıyla kaydedildi!');
      onClose();
    } catch (error: any) {
      alert('Maç kaydedildi ancak istatistikler güncellenirken bir hata oluştu: ' + error.message);
      onClose();
    }
  };

  const getPlayerName = (playerId: string) => {
    return toTurkishUpperCase(getPlayerDisplayName(players, playerId));
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="add-match-modal" onClick={(e) => e.stopPropagation()}>
        <button className="close-button" onClick={onClose}>×</button>
        <h2>Maç Ekle</h2>

        <div className="form-group">
          <label>
            Maç Tarihi:
            <input
              type="date"
              value={matchDate}
              onChange={(e) => setMatchDate(e.target.value)}
              required
            />
          </label>
        </div>

        <div className="form-group">
          <label>
            Sezon:
            <select
              value={selectedSeasonId}
              onChange={(e) => setSelectedSeasonId(e.target.value)}
            >
              {seasons.map(season => (
                <option key={season.id} value={season.id}>{season.name}</option>
              ))}
            </select>
          </label>
        </div>

        <button 
          className="add-event-button"
          onClick={() => setShowEventForm(!showEventForm)}
        >
          + Gol/Asist Ekle
        </button>

        {showEventForm && (
          <div className="event-form">
            <div className="form-group">
              <label>
                Dakika:
                <select
                  value={newEvent.minute || 1}
                  onChange={(e) => setNewEvent({ ...newEvent, minute: parseInt(e.target.value) })}
                  className="minute-picker"
                >
                  {Array.from({ length: 60 }, (_, i) => i + 1).map(minute => (
                    <option key={minute} value={minute}>{minute}</option>
                  ))}
                </select>
              </label>
            </div>

            <div className="form-group">
              <label>
                Takım:
                <select
                  value={newEvent.team}
                  onChange={(e) => setNewEvent({ ...newEvent, team: e.target.value as 'black' | 'white' })}
                >
                  <option value="black">SİYAH</option>
                  <option value="white">BEYAZ</option>
                </select>
              </label>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={isGuestScorer}
                  onChange={(e) => {
                    setIsGuestScorer(e.target.checked);
                    if (e.target.checked) {
                      setNewEvent({ ...newEvent, scorerId: undefined });
                    }
                  }}
                />
                Misafir Oyuncu (Gol)
              </label>
            </div>

            {isGuestScorer ? (
              <div className="form-group">
                <label>
                  Misafir Oyuncu İsmi (Gol):
                  <input
                    type="text"
                    value={newEvent.guestScorerName || ''}
                    onChange={(e) => setNewEvent({ ...newEvent, guestScorerName: e.target.value })}
                    placeholder="Misafir oyuncu ismi"
                  />
                </label>
              </div>
            ) : (
              <div className="form-group">
                <label>
                  Gol Atan:
                  <select
                    value={newEvent.scorerId || ''}
                    onChange={(e) => setNewEvent({ ...newEvent, scorerId: e.target.value })}
                  >
                    <option value="">Seçiniz</option>
                    {[...players].sort((a, b) => turkishSort(a.name, b.name)).map(player => (
                      <option key={player.id} value={player.id}>{toTurkishUpperCase(player.name)}</option>
                    ))}
                  </select>
                </label>
              </div>
            )}

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={isGuestAssist}
                  onChange={(e) => {
                    setIsGuestAssist(e.target.checked);
                    if (e.target.checked) {
                      setNewEvent({ ...newEvent, assistId: undefined });
                    }
                  }}
                />
                Misafir Oyuncu (Asist)
              </label>
            </div>

            {isGuestAssist ? (
              <div className="form-group">
                <label>
                  Misafir Oyuncu İsmi (Asist):
                  <input
                    type="text"
                    value={newEvent.guestAssistName || ''}
                    onChange={(e) => setNewEvent({ ...newEvent, guestAssistName: e.target.value })}
                    placeholder="Misafir oyuncu ismi"
                  />
                </label>
              </div>
            ) : (
              <div className="form-group">
                <label>
                  Asist Yapan:
                  <select
                    value={newEvent.assistId || ''}
                    onChange={(e) => setNewEvent({ ...newEvent, assistId: e.target.value || undefined })}
                  >
                    <option value="">Yok</option>
                    {[...players].sort((a, b) => turkishSort(a.name, b.name)).map(player => (
                      <option key={player.id} value={player.id}>{toTurkishUpperCase(player.name)}</option>
                    ))}
                  </select>
                </label>
              </div>
            )}

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={newEvent.ownGoal || false}
                  onChange={(e) => setNewEvent({ ...newEvent, ownGoal: e.target.checked })}
                />
                Kendi Kalesine
              </label>
            </div>

            <button onClick={addEvent}>Ekle</button>
          </div>
        )}

        <div className="events-list">
          {events.map(event => (
            <div key={event.id} className="event-item">
              {event.minute}' - {event.team === 'black' ? 'SİYAH' : 'BEYAZ'} - 
              {event.guestScorerName ? toTurkishUpperCase(event.guestScorerName) : getPlayerName(event.scorerId || '')} {event.ownGoal && '(KK)'}
              {event.guestAssistName ? ` (Asist: ${toTurkishUpperCase(event.guestAssistName)})` : event.assistId && ` (Asist: ${getPlayerName(event.assistId)})`}
              <button onClick={() => setEvents(events.filter(e => e.id !== event.id))}>Sil</button>
            </div>
          ))}
        </div>

        <div className="teams-selection">
          <div className="team-section">
            <h3>SİYAH Takım</h3>
            {[...players].sort((a, b) => turkishSort(a.name, b.name)).map(player => (
              <label key={player.id}>
                <input
                  type="checkbox"
                  checked={blackTeam?.includes(player.id) || false}
                  onChange={() => togglePlayerInTeam(player.id, 'black')}
                />
                {toTurkishUpperCase(player.name)}
              </label>
            ))}
          </div>

          <div className="team-section">
            <h3>BEYAZ Takım</h3>
            {[...players].sort((a, b) => turkishSort(a.name, b.name)).map(player => (
              <label key={player.id}>
                <input
                  type="checkbox"
                  checked={whiteTeam?.includes(player.id) || false}
                  onChange={() => togglePlayerInTeam(player.id, 'white')}
                />
                {toTurkishUpperCase(player.name)}
              </label>
            ))}
          </div>
        </div>

        <div className="awards-selection">
          <div className="form-group">
            <label>
              Siyah Kart:
              <select
                value={blackCard}
                onChange={(e) => setBlackCard(e.target.value)}
              >
                <option value="">Seçiniz</option>
                {players.map(player => (
                  <option key={player.id} value={player.id}>{toTurkishUpperCase(player.name)}</option>
                ))}
              </select>
            </label>
            {blackCard && (
              <div>
                <label className="image-upload-label">
                  Siyah Kart Görseli:
                  <input
                    type="file"
                    accept="image/*"
                    onChange={async (e) => {
                      if (!e.target.files || !e.target.files[0]) return;
                      const file = e.target.files[0];
                      try {
                        console.log('[AddMatchModal] Siyah kart görseli yükleniyor...');
                        const imageUrl = await storage.uploadImage(file, `matches/cards/black-${Date.now()}-${file.name}`);
                        console.log('[AddMatchModal] Siyah kart görseli yüklendi:', imageUrl);
                        setBlackCardImage(imageUrl);
                        alert('Siyah kart görseli başarıyla yüklendi!');
                      } catch (error: any) {
                        console.error('[AddMatchModal] Siyah kart görseli yükleme hatası:', error);
                        alert('Görsel yüklenirken bir hata oluştu: ' + (error.message || 'Bilinmeyen hata'));
                      }
                    }}
                    style={{ display: 'none' }}
                  />
                </label>
                {blackCardImage && (
                  <img 
                    src={ensureHttps(blackCardImage)} 
                    alt="Siyah Kart" 
                    className="image-preview-small"
                    onError={(e) => {
                      console.error('[AddMatchModal] Siyah kart görseli yüklenemedi:', blackCardImage);
                      const target = e.target as HTMLImageElement;
                      if (blackCardImage.startsWith('data:')) {
                        console.warn('[AddMatchModal] Base64 format tespit edildi, URL formatına çevrilmeli');
                      }
                    }}
                    onLoad={() => {
                      console.log('[AddMatchModal] Siyah kart görseli başarıyla yüklendi');
                    }}
                  />
                )}
              </div>
            )}
          </div>

          <div className="form-group">
            <label>
              MVDP:
              <select
                value={mvdp}
                onChange={(e) => setMvdp(e.target.value)}
              >
                <option value="">Seçiniz</option>
                {players.map(player => (
                  <option key={player.id} value={player.id}>{toTurkishUpperCase(player.name)}</option>
                ))}
              </select>
            </label>
            {mvdp && (
              <div>
                <label className="image-upload-label">
                  MVDP Kart Görseli:
                  <input
                    type="file"
                    accept="image/*"
                    onChange={async (e) => {
                      if (!e.target.files || !e.target.files[0]) return;
                      const file = e.target.files[0];
                      try {
                        console.log('[AddMatchModal] MVDP kart görseli yükleniyor...');
                        const imageUrl = await storage.uploadImage(file, `matches/cards/mvdp-${Date.now()}-${file.name}`);
                        console.log('[AddMatchModal] MVDP kart görseli yüklendi:', imageUrl);
                        setMvdpImage(imageUrl);
                        alert('MVDP kart görseli başarıyla yüklendi!');
                      } catch (error: any) {
                        console.error('[AddMatchModal] MVDP kart görseli yükleme hatası:', error);
                        alert('Görsel yüklenirken bir hata oluştu: ' + (error.message || 'Bilinmeyen hata'));
                      }
                    }}
                    style={{ display: 'none' }}
                  />
                </label>
                {mvdpImage && (
                  <img 
                    src={ensureHttps(mvdpImage)} 
                    alt="MVDP Kart" 
                    className="image-preview-small"
                    onError={(e) => {
                      console.error('[AddMatchModal] MVDP kart görseli yüklenemedi:', mvdpImage);
                      const target = e.target as HTMLImageElement;
                      if (mvdpImage.startsWith('data:')) {
                        console.warn('[AddMatchModal] Base64 format tespit edildi, URL formatına çevrilmeli');
                      }
                    }}
                    onLoad={() => {
                      console.log('[AddMatchModal] MVDP kart görseli başarıyla yüklendi');
                    }}
                  />
                )}
              </div>
            )}
          </div>
        </div>

        <div className="form-group">
          <label className="image-upload-label">
            Maç Kadroları Görseli (Dikey):
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                if (!e.target.files || !e.target.files[0]) return;
                const file = e.target.files[0];
                const reader = new FileReader();
                reader.onload = (event) => {
                  const imageData = event.target?.result as string;
                  setMatchImage(imageData);
                };
                reader.readAsDataURL(file);
              }}
              style={{ display: 'none' }}
            />
          </label>
          {matchImage && (
            <div className="match-image-preview">
              <img src={ensureHttps(matchImage)} alt="Maç Kadroları" className="match-image-vertical" />
            </div>
          )}
        </div>

        <button className="save-match-button" onClick={saveMatch}>
          Maçı Kaydet
        </button>
      </div>
    </div>
  );
};

interface MatchDetailModalProps {
  match: Match;
  seasons: Season[];
  onClose: () => void;
  onPlayerClick: (playerId: string) => void;
  getPlayerName: (playerId: string) => string;
  onEdit?: () => void;
  onDelete?: () => void;
}

const MatchDetailModal: React.FC<MatchDetailModalProps> = ({ match, seasons, onClose, onPlayerClick, getPlayerName, onEdit, onDelete }) => {
  const isAdmin = storage.isAdminMode();
  const matchSeason = seasons.find(s => s.id === match.seasonId);
  const seasonName = matchSeason?.name || '';
  
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="match-detail-modal" onClick={(e) => e.stopPropagation()}>
        <button className="close-button" onClick={onClose}>×</button>
        <h2>Maç Detayları</h2>
        
        {isAdmin && (onEdit || onDelete) && (
          <div className="match-actions">
            {onEdit && (
              <button className="edit-match-button" onClick={onEdit}>
                Düzenle
              </button>
            )}
            {onDelete && (
              <button className="delete-match-button" onClick={onDelete}>
                Sil
              </button>
            )}
          </div>
        )}
        
        <div className="match-info">
          {seasonName && (
            <div className="match-season-large">
              {seasonName}
            </div>
          )}
          <div className="match-date-large">
            {new Date(match.date).toLocaleDateString('tr-TR')}
          </div>
          <div className="match-score-large">
            <TeamLogo team="black" size={56} />
            <span className="team-name">SİYAH</span> {match.blackScore} - {match.whiteScore} <span className="team-name">BEYAZ</span>
            <TeamLogo team="white" size={56} />
          </div>
        </div>

        <div className="match-events-detail">
          <h3>Gol ve Asistler:</h3>
          {[...match.events].sort((a, b) => a.minute - b.minute).map(event => (
            <div key={event.id} className={`event-detail-item event-detail-item-${event.team}`}>
              <span className="minute">{event.minute}'</span>
              <span className="team-badge">
                <TeamLogo team={event.team} size={28} />
              </span>
              <GoalIcon size={16} team={event.team} />
              {event.guestScorerName ? (
                <span className="player-name-black">{toTurkishUpperCase(event.guestScorerName)}{event.ownGoal && ' (KK)'}</span>
              ) : (
                <span 
                  className={`player-name-clickable player-name-${event.team}`}
                  onClick={() => event.scorerId && onPlayerClick(event.scorerId)}
                >
                  {event.scorerId ? getPlayerName(event.scorerId) : ''}{event.ownGoal && ' (KK)'}
                </span>
              )}
              {(event.guestAssistName || event.assistId) && (
                <>
                  <AssistIcon size={16} team={event.team} />
                  {event.guestAssistName ? (
                    <span className="player-name-black">{toTurkishUpperCase(event.guestAssistName)}</span>
                  ) : (
                    <span 
                      className={`player-name-clickable player-name-${event.team}`}
                      onClick={() => event.assistId && onPlayerClick(event.assistId!)}
                    >
                      {event.assistId ? getPlayerName(event.assistId) : ''}
                    </span>
                  )}
                </>
              )}
            </div>
          ))}
        </div>

        {(match.blackCard && match.blackCardImage) || (match.mvdp && match.mvdpImage) ? (
          (match.blackCard && match.blackCardImage && match.mvdp && match.mvdpImage) ? (
            <div className="match-awards-container">
              <div className="match-award-single">
                <div className="award-header">
                  <strong>Siyah Kart: </strong>
                  <span 
                    className="player-name-clickable" 
                    onClick={() => onPlayerClick(match.blackCard!)}
                  >
                    {getPlayerName(match.blackCard)}
                  </span>
                </div>
                <div className="award-image-container-square">
                  <img src={ensureHttps(match.blackCardImage)} alt="Siyah Kart" className="award-image-square" />
                </div>
              </div>
              <div className="match-award-single">
                <div className="award-header">
                  <strong>MVDP: </strong>
                  <span 
                    className="player-name-clickable" 
                    onClick={() => onPlayerClick(match.mvdp!)}
                  >
                    {getPlayerName(match.mvdp)}
                  </span>
                </div>
                <div className="award-image-container-square">
                  <img src={ensureHttps(match.mvdpImage)} alt="MVDP Kart" className="award-image-square" />
                </div>
              </div>
            </div>
          ) : (
            <>
              {match.blackCard && match.blackCardImage && (
                <div className="match-award">
                  <div className="award-header">
                    <strong>Siyah Kart: </strong>
                    <span 
                      className="player-name-clickable" 
                      onClick={() => onPlayerClick(match.blackCard!)}
                    >
                      {getPlayerName(match.blackCard)}
                    </span>
                  </div>
                  <div className="award-image-container">
                    <img src={ensureHttps(match.blackCardImage)} alt="Siyah Kart" className="award-image" />
                  </div>
                </div>
              )}
              {match.mvdp && match.mvdpImage && (
                <div className="match-award">
                  <div className="award-header">
                    <strong>MVDP Kart: </strong>
                    <span 
                      className="player-name-clickable" 
                      onClick={() => onPlayerClick(match.mvdp!)}
                    >
                      {getPlayerName(match.mvdp)}
                    </span>
                  </div>
                  <div className="award-image-container">
                    <img src={ensureHttps(match.mvdpImage)} alt="MVDP Kart" className="award-image" />
                  </div>
                </div>
              )}
            </>
          )
        ) : null}

        {match.matchImage && (
          <div className="match-image-container">
            <img src={ensureHttps(match.matchImage)} alt="Maç Kadroları" className="match-image-vertical" />
          </div>
        )}
      </div>
    </div>
  );
};

interface EditMatchModalProps {
  match: Match;
  onClose: () => void;
  seasons: Season[];
  players: Player[];
}

const EditMatchModal: React.FC<EditMatchModalProps> = ({ match, onClose, seasons, players }) => {
  const [matchDate, setMatchDate] = useState<string>(match.date || '');
  const [selectedSeasonId, setSelectedSeasonId] = useState<string>(match.seasonId || '');
  const [blackTeam, setBlackTeam] = useState<string[]>(match.blackTeam || []);
  const [whiteTeam, setWhiteTeam] = useState<string[]>(match.whiteTeam || []);
  const [events, setEvents] = useState<MatchEvent[]>(match.events || []);
  const [blackCard, setBlackCard] = useState<string>(match.blackCard || '');
  const [mvdp, setMvdp] = useState<string>(match.mvdp || '');
  const [blackCardImage, setBlackCardImage] = useState<string>(match.blackCardImage || '');
  const [mvdpImage, setMvdpImage] = useState<string>(match.mvdpImage || '');
  const [matchImage, setMatchImage] = useState<string>(match.matchImage || '');
  const [showEventForm, setShowEventForm] = useState(false);
  const [newEvent, setNewEvent] = useState<Partial<MatchEvent>>({
    minute: 1,
    team: 'black',
    ownGoal: false,
  });
  const [isGuestScorer, setIsGuestScorer] = useState(false);
  const [isGuestAssist, setIsGuestAssist] = useState(false);

  const togglePlayerInTeam = (playerId: string, team: 'black' | 'white') => {
    if (team === 'black') {
      setBlackTeam(prev => {
        const currentTeam = prev || [];
        return currentTeam.includes(playerId) 
          ? currentTeam.filter(id => id !== playerId)
          : [...currentTeam, playerId];
      });
    } else {
      setWhiteTeam(prev => {
        const currentTeam = prev || [];
        return currentTeam.includes(playerId) 
          ? currentTeam.filter(id => id !== playerId)
          : [...currentTeam, playerId];
      });
    }
  };

  const addEvent = () => {
    if (!newEvent.scorerId) return;
    
    const event: MatchEvent = {
      id: `event-${Date.now()}`,
      minute: newEvent.minute || 1,
      team: newEvent.team || 'black',
      scorerId: newEvent.scorerId,
      assistId: newEvent.assistId,
      ownGoal: newEvent.ownGoal || false,
    };
    
    setEvents([...events, event]);
    setNewEvent({
      minute: 1,
      team: 'black',
      ownGoal: false,
    });
    setShowEventForm(false);
  };

  const handleBlackCardImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0]) return;
    const file = e.target.files[0];
    try {
      console.log('[EditMatchModal] Siyah kart görseli yükleniyor...');
      const imageUrl = await storage.uploadImage(file, `matches/cards/black-${Date.now()}-${file.name}`);
      console.log('[EditMatchModal] Siyah kart görseli yüklendi:', imageUrl);
      setBlackCardImage(imageUrl);
      alert('Siyah kart görseli başarıyla yüklendi!');
    } catch (error: any) {
      console.error('[EditMatchModal] Siyah kart görseli yükleme hatası:', error);
      alert('Görsel yüklenirken bir hata oluştu: ' + (error.message || 'Bilinmeyen hata'));
    }
  };

  const handleMvdpImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0]) return;
    const file = e.target.files[0];
    try {
      console.log('[EditMatchModal] MVDP kart görseli yükleniyor...');
      const imageUrl = await storage.uploadImage(file, `matches/cards/mvdp-${Date.now()}-${file.name}`);
      console.log('[EditMatchModal] MVDP kart görseli yüklendi:', imageUrl);
      setMvdpImage(imageUrl);
      alert('MVDP kart görseli başarıyla yüklendi!');
    } catch (error: any) {
      console.error('[EditMatchModal] MVDP kart görseli yükleme hatası:', error);
      alert('Görsel yüklenirken bir hata oluştu: ' + (error.message || 'Bilinmeyen hata'));
    }
  };

  const saveMatch = async () => {
    if (!matchDate || !selectedSeasonId) {
      alert('Lütfen tarih ve sezon seçin!');
      return;
    }

    // Kendi kalesine goller de skora dahil edilir (oyuncu istatistiğine yansımaz ama skora yansır)
    const blackScore = events.filter(e => 
      e.team === 'black'
    ).length;
    const whiteScore = events.filter(e => 
      e.team === 'white'
    ).length;

    // Events'teki undefined değerleri temizle
    const cleanedEvents = events.map(event => {
      const cleaned: any = {
        id: event.id,
        minute: event.minute,
        team: event.team,
        ownGoal: event.ownGoal,
      };
      if (event.scorerId) cleaned.scorerId = event.scorerId;
      if (event.assistId) cleaned.assistId = event.assistId;
      if (event.guestScorerName) cleaned.guestScorerName = event.guestScorerName;
      if (event.guestAssistName) cleaned.guestAssistName = event.guestAssistName;
      return cleaned;
    });

    const updatedMatch: Match = {
      ...match,
      id: match.id,
      date: matchDate,
      seasonId: selectedSeasonId,
      blackTeam,
      whiteTeam,
      blackScore,
      whiteScore,
      events: cleanedEvents,
    };
    
    // Opsiyonel alanları sadece varsa ekle
    if (blackCard) updatedMatch.blackCard = blackCard;
    else delete (updatedMatch as any).blackCard;
    if (mvdp) updatedMatch.mvdp = mvdp;
    else delete (updatedMatch as any).mvdp;
    if (blackCardImage) updatedMatch.blackCardImage = blackCardImage;
    else delete (updatedMatch as any).blackCardImage;
    if (mvdpImage) updatedMatch.mvdpImage = mvdpImage;
    else delete (updatedMatch as any).mvdpImage;
    if (matchImage) updatedMatch.matchImage = matchImage;
    else delete (updatedMatch as any).matchImage;

    // İstatistikleri güncelle
    await updateMatchStats(match, updatedMatch);

    // Maçı güncelle
    try {
      const allMatches = await storage.getMatches({ forceRefresh: true });
      const updatedMatches = allMatches.map(m => 
        m.id === match.id ? updatedMatch : m
      );
      await storage.saveMatches(updatedMatches);
    } catch (error: any) {
      if (error.name === 'QuotaExceededError' || error.message?.includes('quota')) {
        alert('Depolama alanı dolu! Lütfen eski maçlardaki görselleri silin veya görselleri daha küçük boyutlarda yükleyin.');
        return;
      }
      throw error;
    }

    // Kart görsellerini oyunculara ekle
    if (blackCard && blackCardImage) {
      const allPlayers = await storage.getPlayers({ forceRefresh: true });
      const updatedPlayers = allPlayers.map(p => {
        if (p.id === blackCard) {
          const currentSeasonId = selectedSeasonId;
          const currentCards = Array.isArray(p.playerCards) 
            ? {} 
            : (p.playerCards as { [seasonId: string]: string[] }) || {};
          const seasonCards = currentCards[currentSeasonId] || [];
          if (!seasonCards.includes(blackCardImage)) {
            return {
              ...p,
              playerCards: {
                ...currentCards,
                [currentSeasonId]: [...seasonCards, blackCardImage]
              }
            };
          }
        }
        return p;
      });
      await storage.savePlayers(updatedPlayers);
    }

    if (mvdp && mvdpImage) {
      const allPlayers = await storage.getPlayers({ forceRefresh: true });
      const updatedPlayers = allPlayers.map(p => {
        if (p.id === mvdp) {
          const currentSeasonId = selectedSeasonId;
          const currentCards = Array.isArray(p.playerCards) 
            ? {} 
            : (p.playerCards as { [seasonId: string]: string[] }) || {};
          const seasonCards = currentCards[currentSeasonId] || [];
          if (!seasonCards.includes(mvdpImage)) {
            return {
              ...p,
              playerCards: {
                ...currentCards,
                [currentSeasonId]: [...seasonCards, mvdpImage]
              }
            };
          }
        }
        return p;
      });
      await storage.savePlayers(updatedPlayers);
    }

    alert('Maç başarıyla güncellendi!');
    onClose();
  };

  const getPlayerName = (playerId: string) => {
    return toTurkishUpperCase(getPlayerDisplayName(players, playerId));
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="add-match-modal" onClick={(e) => e.stopPropagation()}>
        <button className="close-button" onClick={onClose}>×</button>
        <h2>Maç Düzenle</h2>

        <div className="form-group">
          <label>
            Maç Tarihi:
            <input
              type="date"
              value={matchDate}
              onChange={(e) => setMatchDate(e.target.value)}
              required
            />
          </label>
        </div>

        <div className="form-group">
          <label>
            Sezon:
            <select
              value={selectedSeasonId}
              onChange={(e) => setSelectedSeasonId(e.target.value)}
            >
              {seasons.map(season => (
                <option key={season.id} value={season.id}>{season.name}</option>
              ))}
            </select>
          </label>
        </div>

        <button 
          className="add-event-button"
          onClick={() => setShowEventForm(!showEventForm)}
        >
          + Gol/Asist Ekle
        </button>

        {showEventForm && (
          <div className="event-form">
            <div className="form-group">
              <label>
                Dakika:
                <select
                  value={newEvent.minute || 1}
                  onChange={(e) => setNewEvent({ ...newEvent, minute: parseInt(e.target.value) })}
                  className="minute-picker"
                >
                  {Array.from({ length: 60 }, (_, i) => i + 1).map(minute => (
                    <option key={minute} value={minute}>{minute}</option>
                  ))}
                </select>
              </label>
            </div>

            <div className="form-group">
              <label>
                Takım:
                <select
                  value={newEvent.team}
                  onChange={(e) => setNewEvent({ ...newEvent, team: e.target.value as 'black' | 'white' })}
                >
                  <option value="black">SİYAH</option>
                  <option value="white">BEYAZ</option>
                </select>
              </label>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={isGuestScorer}
                  onChange={(e) => {
                    setIsGuestScorer(e.target.checked);
                    if (e.target.checked) {
                      setNewEvent({ ...newEvent, scorerId: undefined });
                    }
                  }}
                />
                Misafir Oyuncu (Gol)
              </label>
            </div>

            {isGuestScorer ? (
              <div className="form-group">
                <label>
                  Misafir Oyuncu İsmi (Gol):
                  <input
                    type="text"
                    value={newEvent.guestScorerName || ''}
                    onChange={(e) => setNewEvent({ ...newEvent, guestScorerName: e.target.value })}
                    placeholder="Misafir oyuncu ismi"
                  />
                </label>
              </div>
            ) : (
              <div className="form-group">
                <label>
                  Gol Atan:
                  <select
                    value={newEvent.scorerId || ''}
                    onChange={(e) => setNewEvent({ ...newEvent, scorerId: e.target.value })}
                  >
                    <option value="">Seçiniz</option>
                    {[...players].sort((a, b) => turkishSort(a.name, b.name)).map(player => (
                      <option key={player.id} value={player.id}>{toTurkishUpperCase(player.name)}</option>
                    ))}
                  </select>
                </label>
              </div>
            )}

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={isGuestAssist}
                  onChange={(e) => {
                    setIsGuestAssist(e.target.checked);
                    if (e.target.checked) {
                      setNewEvent({ ...newEvent, assistId: undefined });
                    }
                  }}
                />
                Misafir Oyuncu (Asist)
              </label>
            </div>

            {isGuestAssist ? (
              <div className="form-group">
                <label>
                  Misafir Oyuncu İsmi (Asist):
                  <input
                    type="text"
                    value={newEvent.guestAssistName || ''}
                    onChange={(e) => setNewEvent({ ...newEvent, guestAssistName: e.target.value })}
                    placeholder="Misafir oyuncu ismi"
                  />
                </label>
              </div>
            ) : (
              <div className="form-group">
                <label>
                  Asist Yapan:
                  <select
                    value={newEvent.assistId || ''}
                    onChange={(e) => setNewEvent({ ...newEvent, assistId: e.target.value || undefined })}
                  >
                    <option value="">Yok</option>
                    {[...players].sort((a, b) => turkishSort(a.name, b.name)).map(player => (
                      <option key={player.id} value={player.id}>{toTurkishUpperCase(player.name)}</option>
                    ))}
                  </select>
                </label>
              </div>
            )}

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={newEvent.ownGoal || false}
                  onChange={(e) => setNewEvent({ ...newEvent, ownGoal: e.target.checked })}
                />
                Kendi Kalesine
              </label>
            </div>

            <button onClick={addEvent}>Ekle</button>
          </div>
        )}

        <div className="events-list">
          {events.map(event => (
            <div key={event.id} className="event-item">
              {event.minute}' - {event.team === 'black' ? 'SİYAH' : 'BEYAZ'} - 
              {event.guestScorerName ? toTurkishUpperCase(event.guestScorerName) : getPlayerName(event.scorerId || '')} {event.ownGoal && '(KK)'}
              {event.guestAssistName ? ` (Asist: ${toTurkishUpperCase(event.guestAssistName)})` : event.assistId && ` (Asist: ${getPlayerName(event.assistId)})`}
              <button onClick={() => setEvents(events.filter(e => e.id !== event.id))}>Sil</button>
            </div>
          ))}
        </div>

        <div className="teams-selection">
          <div className="team-section">
            <h3>SİYAH Takım</h3>
            {[...players].sort((a, b) => turkishSort(a.name, b.name)).map(player => (
              <label key={player.id}>
                <input
                  type="checkbox"
                  checked={blackTeam?.includes(player.id) || false}
                  onChange={() => togglePlayerInTeam(player.id, 'black')}
                />
                {toTurkishUpperCase(player.name)}
              </label>
            ))}
          </div>

          <div className="team-section">
            <h3>BEYAZ Takım</h3>
            {[...players].sort((a, b) => turkishSort(a.name, b.name)).map(player => (
              <label key={player.id}>
                <input
                  type="checkbox"
                  checked={whiteTeam?.includes(player.id) || false}
                  onChange={() => togglePlayerInTeam(player.id, 'white')}
                />
                {toTurkishUpperCase(player.name)}
              </label>
            ))}
          </div>
        </div>

        <div className="awards-selection">
          <div className="form-group">
            <label>
              Siyah Kart:
              <select
                value={blackCard}
                onChange={(e) => setBlackCard(e.target.value)}
              >
                <option value="">Seçiniz</option>
                {players.map(player => (
                  <option key={player.id} value={player.id}>{toTurkishUpperCase(player.name)}</option>
                ))}
              </select>
            </label>
            {blackCard && (
              <div>
                <label className="image-upload-label">
                  Siyah Kart Görseli:
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleBlackCardImageUpload}
                    style={{ display: 'none' }}
                  />
                </label>
                {blackCardImage && (
                  <img 
                    src={ensureHttps(blackCardImage)} 
                    alt="Siyah Kart" 
                    className="image-preview-small"
                    onError={(e) => {
                      console.error('[EditMatchModal] Siyah kart görseli yüklenemedi:', blackCardImage);
                      if (blackCardImage.startsWith('data:')) {
                        console.warn('[EditMatchModal] Base64 format tespit edildi, URL formatına çevrilmeli');
                      }
                    }}
                    onLoad={() => {
                      console.log('[EditMatchModal] Siyah kart görseli başarıyla yüklendi');
                    }}
                  />
                )}
              </div>
            )}
          </div>

          <div className="form-group">
            <label>
              MVDP:
              <select
                value={mvdp}
                onChange={(e) => setMvdp(e.target.value)}
              >
                <option value="">Seçiniz</option>
                {players.map(player => (
                  <option key={player.id} value={player.id}>{toTurkishUpperCase(player.name)}</option>
                ))}
              </select>
            </label>
            {mvdp && (
              <div>
                <label className="image-upload-label">
                  MVDP Kart Görseli:
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleMvdpImageUpload}
                    style={{ display: 'none' }}
                  />
                </label>
                {mvdpImage && (
                  <img 
                    src={ensureHttps(mvdpImage)} 
                    alt="MVDP Kart" 
                    className="image-preview-small"
                    onError={(e) => {
                      console.error('[EditMatchModal] MVDP kart görseli yüklenemedi:', mvdpImage);
                      if (mvdpImage.startsWith('data:')) {
                        console.warn('[EditMatchModal] Base64 format tespit edildi, URL formatına çevrilmeli');
                      }
                    }}
                    onLoad={() => {
                      console.log('[EditMatchModal] MVDP kart görseli başarıyla yüklendi');
                    }}
                  />
                )}
              </div>
            )}
          </div>
        </div>

        <div className="form-group">
          <label className="image-upload-label">
            Maç Kadroları Görseli (Dikey):
            <input
              type="file"
              accept="image/*"
              onChange={async (e) => {
                if (!e.target.files || !e.target.files[0]) return;
                const file = e.target.files[0];
                try {
                  console.log('[EditMatchModal] Maç kadro görseli yükleniyor...');
                  const imageUrl = await storage.uploadImage(file, `matches/lineups/${Date.now()}-${file.name}`);
                  console.log('[EditMatchModal] Maç kadro görseli yüklendi:', imageUrl);
                  setMatchImage(imageUrl);
                  alert('Maç kadro görseli başarıyla yüklendi!');
                } catch (error: any) {
                  console.error('[EditMatchModal] Maç kadro görseli yükleme hatası:', error);
                  alert('Görsel yüklenirken bir hata oluştu: ' + (error.message || 'Bilinmeyen hata'));
                }
              }}
              style={{ display: 'none' }}
            />
          </label>
          {matchImage && (
            <div className="match-image-preview">
              <img 
                src={ensureHttps(matchImage)} 
                alt="Maç Kadroları" 
                className="match-image-vertical"
                onError={(e) => {
                  console.error('[EditMatchModal] Maç kadro görseli yüklenemedi:', matchImage);
                  if (matchImage.startsWith('data:')) {
                    console.warn('[EditMatchModal] Base64 format tespit edildi, URL formatına çevrilmeli');
                  }
                }}
                onLoad={() => {
                  console.log('[EditMatchModal] Maç kadro görseli başarıyla yüklendi');
                }}
              />
            </div>
          )}
        </div>

        <button className="save-match-button" onClick={saveMatch}>
          Maçı Güncelle
        </button>
      </div>
    </div>
  );
};

export default HistoryPage;

